import axiosLib from "axios";
import { store } from "../State/store/configureStore";
import { showToaster } from "../State/actions/toaster";
import { LOG_OUT } from "../State/actions/actionTypes";
import StorageService from "../services/storageService";
import { isAnalyticsRequest } from "./isAnalyticsRequest";
export const API_URL_KEY = "API_URL";

export class AxiosInstance {
  constructor() {
    this.axios = axiosLib.create();
    this.axios.defaults.timeout = 200000;
    // this.axios.defaults.baseURL = API_URL;
  }

  setToken(token) {
    this.axios.defaults.headers.common.Authorization = "Bearer " + token;
  }

  setBaseURL(url) {
    this.axios.defaults.baseURL = url;
  }

  removeToken() {
    delete this.axios.defaults.headers.common.Authorization;
  }

  /**
   * Makes an HTTP request using axios
   * @param {string} url - The URL to make the request to
   * @param {string} method - The HTTP method (get, post, put, patch, delete)
   * @param {any} data - The data to send with the request
   * @param {object} headers - Custom headers for the request
   * @param {function} fileUploadProgress - Callback function for upload progress events
   * @param {number} timeout - Custom timeout for the request (in milliseconds)
   * @param {string} responseType - Custom response type (e.g., "blob" for file downloads)
   * @param {boolean} noAlert - Whether to suppress error alerts
   * @returns {Promise<any>} Promise that resolves with the response data
   */
  async request(
    url,
    method = "get",
    data,
    headers,
    fileUploadProgress,
    timeout,
    responseType,
    noAlert
  ) {
    // Configure request options
    let options = {
      url: url,
      method,
    };

    if (data) {
      options.data = data;
    }

    if (headers) {
      options.headers = headers;
    }

    if (timeout) {
      options.timeout = 120000;
    }

    if (fileUploadProgress) {
      options.onUploadProgress = fileUploadProgress;
    }

    if (responseType) {
      options.responseType = "blob";
    }

    try {
      // Make the request
      const response = await this.axios.request(options);
      return response.data;
    } catch (error) {
      const response = error.response;

      // Handle various error scenarios
      if (response) {
        const status = response.status;

        // Handle 401 Unauthorized (session expired)
        //
        // Only the primary API is session-authoritative. The analytics service is a
        // secondary, non-critical source whose callers already fall back to Java
        // (see dashboardService.getPatientStatsForStaff). A 401 from it means its own
        // token validation failed — e.g. a JWT signing-secret mismatch between the two
        // services — which must NOT eject a clinician from a live session. Doing so
        // turns an analytics misconfiguration into a clinical-workflow outage.
        if (status === 401) {
          if (isAnalyticsRequest(url)) {
            return; // resolve undefined; the caller falls back
          }
          store.dispatch({ type: LOG_OUT });
          store.dispatch(
            showToaster(
              "Invalid/Expired Session. Please login to continue.",
              "error",
              7
            )
          );
          return; // Special case: resolve with undefined for 401
        }

        // Handle MFA required case
        if (response.data && response.data.error === "mfa_required") {
          return response.data;
        }

        // Extract error message and code
        const msg = response.data
          ? response.data.errorMessage ||
            response.data.message ||
            response.data.error_description ||
            response.data
          : null;

        const errorCode = response.data ? response.data.errorCode : null;

        // Handle specific error cases
        if (noAlert) {
          throw { showError: true, message: msg, errorCode };
        }

        let showError = true;
        let errorMessage = msg.error || msg || "Unexpected problem occurred. Please try again.";

        if (msg && status === 500) {
          showError = false;
          store.dispatch(showToaster("Error occurred: " + errorMessage, "error", 7));
        } else if (msg && status === 400) {
          showError = false;
          store.dispatch(showToaster("Error occurred: " + errorMessage, "error", 7));
        }

        throw { showError, message: msg, errorCode };
      } else if (error.message) {
        // Handle network errors
        store.dispatch(showToaster(error.message, "error", 7));
        throw { showError: false };
      } else {
        // Handle unknown errors
        throw { showError: true };
      }
    }
  }
}
//SSE Class
export class FetchSseClient {
  constructor({ getToken, getBaseURL }) {
    this.getToken = getToken;
    this.getBaseURL = getBaseURL;
    this.eventSources = new Map(); // Track active EventSource connections
  }

  async postSse(path, payload, onEvent, onError, onHttpStreamComplete) {
    const token = this.getToken();
    const baseURL = this.getBaseURL();

    if (!baseURL) {
      const err = new Error(
        "SSE error: Base URL is not available. Ensure API_URL_KEY is set in local storage."
      );
      console.error(err.message);
      if (onError) onError(err);
      return;
    }

    let res;
    try {
      res = await fetch(baseURL + path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(payload),
      });
    } catch (fetchError) {
      console.error("SSE fetch failed:", fetchError);
      if (onError) onError(fetchError);
      return;
    }

    if (!res.ok || !res.body) {
      const text = await res
        .text()
        .catch(() => "Failed to read error response body");
      const err = new Error(`SSE error ${res.status}: ${text}`);
      console.error(err.message);
      if (onError) onError(err);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const read = async () => {
      try {
        const { done, value } = await reader.read();
        if (done) {
          if (onHttpStreamComplete) onHttpStreamComplete();
          return;
        }

        buffer += decoder.decode(value, { stream: true });
        const messageBlocks = buffer.split(/\r?\n\r?\n/);

        buffer = messageBlocks.pop() || "";

        messageBlocks.forEach((block) => {
          if (!block.trim()) return;

          const lines = block.split(/\r?\n/);
          let eventName = "";
          let dataAccumulator = [];

          lines.forEach((line) => {
            if (line.startsWith("event:")) {
              eventName = line.substring("event:".length).trim();
            } else if (line.startsWith("data:")) {
              dataAccumulator.push(line.substring("data:".length).trim());
            }
          });

          const dataPayload = dataAccumulator.join("\n");

          if (eventName === "complete") {
            onEvent({ event: eventName, data: dataPayload }); // Pass raw "END"
          } else if (eventName === "audio") {
            onEvent({ event: eventName, data: dataPayload }); // Pass raw base64 audio
          } else if (dataPayload) {
            try {
              const parsedJson = JSON.parse(dataPayload);
              onEvent({ event: eventName || "message", data: parsedJson });
            } catch (e) {
              console.error(
                `SSE JSON.parse error in FetchSseClient for event '${
                  eventName || "message"
                }':`,
                e,
                "Raw data:",
                dataPayload
              );
              if (onError) {
                onError(
                  new Error(
                    `Failed to parse JSON for event '${
                      eventName || "message"
                    }'. Data: "${dataPayload.slice(0, 100)}..."`
                  )
                );
              }
            }
          } else if (eventName) {
            onEvent({ event: eventName, data: null });
          }
        });

        return read();
      } catch (streamError) {
        console.error("SSE stream reading error:", streamError);
        if (onError) onError(streamError);
        reader
          .cancel()
          .catch((cancelError) =>
            console.error("Error cancelling reader:", cancelError)
          );
      }
    };

    read().catch((err) => {
      console.error("Unhandled error in SSE read chain:", err);
      if (onError) onError(err);
    });
  }

  // GET SSE using fetch (similar to postSse but with GET)
  async getSse(path, handlers = {}) {
    const token = this.getToken();
    const baseURL = this.getBaseURL();

    if (!baseURL) {
      const err = new Error(
        "SSE error: Base URL is not available. Ensure API_URL_KEY is set in local storage."
      );
      console.error(err.message);
      if (handlers.onError) handlers.onError(err);
      return null;
    }

    // Close existing connection if any
    if (this.eventSources.has(path)) {
      const existing = this.eventSources.get(path);
      if (existing && existing.abort) {
        existing.abort();
      }
      this.eventSources.delete(path);
    }

    const abortController = new AbortController();

    try {
      const res = await fetch(baseURL + path, {
        method: "GET",
        headers: {
          Accept: "text/event-stream",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        signal: abortController.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res
          .text()
          .catch(() => "Failed to read error response body");
        const err = new Error(`SSE error ${res.status}: ${text}`);
        console.error(err.message);
        if (handlers.onError) handlers.onError(err);
        return null;
      }

      // Store the abort controller for cleanup
      this.eventSources.set(path, { abort: () => abortController.abort() });

      // Notify connection opened
      console.log(`SSE: Connected to ${path}`);
      if (handlers.onOpen) handlers.onOpen();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const read = async () => {
        try {
          const { done, value } = await reader.read();
          if (done) {
            console.log(`SSE: Connection closed for ${path}`);
            this.eventSources.delete(path);
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const messageBlocks = buffer.split(/\r?\n\r?\n/);

          buffer = messageBlocks.pop() || "";

          messageBlocks.forEach((block) => {
            if (!block.trim()) return;

            const lines = block.split(/\r?\n/);
            let eventName = "";
            let dataAccumulator = [];

            lines.forEach((line) => {
              if (line.startsWith("event:")) {
                eventName = line.substring("event:".length).trim();
              } else if (line.startsWith("data:")) {
                dataAccumulator.push(line.substring("data:".length).trim());
              }
            });

            const dataPayload = dataAccumulator.join("\n");

            if (dataPayload) {
              try {
                const parsedJson = JSON.parse(dataPayload);

                // Handle custom events
                if (
                  eventName &&
                  handlers.events &&
                  handlers.events[eventName]
                ) {
                  handlers.events[eventName](parsedJson);
                }
                // Handle generic messages
                else if (handlers.onMessage) {
                  handlers.onMessage({
                    event: eventName || "message",
                    data: parsedJson,
                  });
                }
              } catch (e) {
                console.error(
                  `SSE JSON.parse error for event '${eventName || "message"}':`,
                  e,
                  "Raw data:",
                  dataPayload
                );
              }
            } else if (
              eventName &&
              handlers.events &&
              handlers.events[eventName]
            ) {
              // Event without data
              handlers.events[eventName](null);
            }
          });

          return read();
        } catch (streamError) {
          if (streamError.name === "AbortError") {
            console.log(`SSE: Connection aborted for ${path}`);
          } else {
            console.error(`SSE stream reading error for ${path}:`, streamError);
            if (handlers.onError) handlers.onError(streamError);
          }
          reader
            .cancel()
            .catch((cancelError) =>
              console.error("Error cancelling reader:", cancelError)
            );
          this.eventSources.delete(path);
        }
      };

      read().catch((err) => {
        console.error("Unhandled error in SSE read chain:", err);
        if (handlers.onError) handlers.onError(err);
        this.eventSources.delete(path);
      });

      // Return the abort controller for cleanup
      return { abort: () => abortController.abort() };
    } catch (error) {
      console.error("SSE: Failed to connect:", error);
      if (handlers.onError) handlers.onError(error);
      return null;
    }
  }

  // Close a specific SSE connection
  closeSse(path) {
    if (this.eventSources.has(path)) {
      const connection = this.eventSources.get(path);
      if (connection && connection.abort) {
        connection.abort();
      } else if (connection && connection.close) {
        connection.close();
      }
      this.eventSources.delete(path);
      console.log(`SSE: Closed connection to ${path}`);
    }
  }

  // Close all SSE connections
  closeAllSse() {
    this.eventSources.forEach((connection, path) => {
      if (connection && connection.abort) {
        connection.abort();
      } else if (connection && connection.close) {
        connection.close();
      }
      console.log(`SSE: Closed connection to ${path}`);
    });
    this.eventSources.clear();
  }
}

export const sse = new FetchSseClient({
  getToken: () => {
    try {
      const header = axiosInstance.axios.defaults.headers.common.Authorization;
      return header?.replace(/^Bearer\s+/, "");
    } catch (e) {
      console.error("Error getting token for SSE:", e);
      return null;
    }
  },
  getBaseURL: () => {
    try {
      return StorageService.getFromLocalStorage(API_URL_KEY);
    } catch (e) {
      console.error("Error getting base URL for SSE:", e);
      return null;
    }
  },
});

let axiosInstance = new AxiosInstance();
let apiURL = StorageService.getFromLocalStorage(API_URL_KEY);
axiosInstance.setBaseURL(apiURL);
// export let axiosInstance = axos;
// have to export the raw axios from axios because of a bug in karma test server with moxios
// a.k.a. axios in test should be just axios (we don't need x-bearer at least for starters)
export let http = axiosInstance;
